import {
  Cross2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { Box, Flex, Grid, IconButton, Spinner, Text } from '@radix-ui/themes';
import { CollapseAllIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTooltip } from 'components/common/tooltip';
import { t } from 'i18next';
import { ISidebarFilters } from 'interfaces/pitch-lists/i-sidebar-filters';
import {
  PitchListCategory,
  PitchListOwner,
} from 'lib_ts/enums/pitch-list.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { first } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import useListLibraryStore from 'stores/use-list-library-store';

export const Controls = (props: {
  onCreate: () => void;
  onRefresh: () => void;
}) => {
  const navigate = useNavigate();

  const sidebarFilters = useListLibraryStore((s) => s.sidebarFilters);
  const loading = useListLibraryStore((s) => s.loading);

  const mergeSidebarFilters = (partial: Partial<ISidebarFilters>) => {
    useListLibraryStore.setState((prev) => ({
      sidebarFilters: {
        ...prev.sidebarFilters,
        ...partial,
      },
    }));
  };

  return (
    <ErrorBoundary identifier="Controls">
      <Flex direction="column" gap="1" p="2" pt="1">
        <Flex gap="2" justify="between" align="center">
          <Box flexGrow="1" minWidth="0">
            <Text
              size="1"
              className="cursor-pointer"
              onClick={() => navigate(paths.pitches.lists)}
              truncate
            >
              {t('main.pitch-lists')}
            </Text>
          </Box>

          <Box>
            <CommonTooltip
              text="pl.collapse-all"
              trigger={
                <IconButton
                  className="btn-floating"
                  size="1"
                  variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                  color={RADIX.COLOR.NEUTRAL}
                  onClick={() =>
                    useListLibraryStore.setState({ sidebarOpenPaths: {} })
                  }
                >
                  <CollapseAllIcon />
                </IconButton>
              }
            />
          </Box>
          <Box>
            <CommonTooltip
              text="pl.create-new-list"
              trigger={
                <IconButton
                  className="btn-floating"
                  size="1"
                  variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                  color={RADIX.COLOR.NEUTRAL}
                  onClick={props.onCreate}
                >
                  <PlusIcon />
                </IconButton>
              }
            />
          </Box>
          <Box>
            <CommonTooltip
              text="pl.refresh-lists"
              trigger={
                <IconButton
                  className="btn-floating"
                  size="1"
                  variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                  color={RADIX.COLOR.NEUTRAL}
                  onClick={props.onRefresh}
                >
                  {loading ? <Spinner /> : <ReloadIcon />}
                </IconButton>
              }
            />
          </Box>
        </Flex>

        <Grid columns="2" gap="2" align="center">
          {/* search bar */}
          <Box>
            <CommonTextInput
              id="pitch-lists-search"
              size="1"
              leftSlot={
                <CommonTooltip
                  text="pl.search-lists"
                  trigger={<MagnifyingGlassIcon className="cursor-pointer" />}
                />
              }
              rightSlot={
                sidebarFilters.search ? (
                  <Cross2Icon
                    className="cursor-pointer"
                    onClick={() => mergeSidebarFilters({ search: '' })}
                  />
                ) : undefined
              }
              value={sidebarFilters.search}
              onChange={(v) => mergeSidebarFilters({ search: v ?? '' })}
              debounceMs={300}
            />
          </Box>

          {/* root dropdown */}
          <Box>
            <CommonSearchInput
              id="pitch-lists-root"
              size="1"
              btnSize="1"
              values={sidebarFilters.category ? [sidebarFilters.category] : []}
              onChange={(values) =>
                mergeSidebarFilters({
                  category: first(values) as PitchListCategory,
                })
              }
              placeholder="All Lists"
              options={[
                {
                  label: 'common.personal',
                  value: PitchListOwner.User,
                },
                {
                  label: 'common.team',
                  value: PitchListOwner.Team,
                },
                {
                  label: 'common.machine',
                  value: PitchListOwner.Machine,
                },
                // todo: add options for filtering by list type, if necessary
              ]}
              hideSearch
              optional
            />
          </Box>
        </Grid>
      </Flex>
    </ErrorBoundary>
  );
};
